import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import {
  FileText, Star, Users, Mail, Search, Plus, Edit2, Trash2,
  ChevronDown, X, ArrowUpRight,
} from 'lucide-react';

const TYPE_COLORS = {
  Note:      'bg-blue-50 text-blue-700 border-blue-200',
  Important: 'bg-amber-50 text-amber-700 border-amber-200',
  Meeting:   'bg-purple-50 text-purple-700 border-purple-200',
  Letter:    'bg-green-50 text-green-700 border-green-200',
};

const inputCls = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white';

/* Searchable member dropdown */
function MemberSelect({ members, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  const selected = members.find(m => String(m.id) === String(value));
  const filtered = members.filter(m => {
    const text = `${m.name} ${m.membershipRef || ''}`.toLowerCase();
    return text.includes(q.toLowerCase());
  }).slice(0, 80);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className={`${inputCls} flex items-center justify-between text-left ${!value ? 'text-slate-400' : 'text-slate-800'}`}>
        {selected ? (
          <span>{selected.name} <span className="text-slate-400 text-xs">#{selected.membershipRef}</span></span>
        ) : 'Select member…'}
        <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input autoFocus value={q} onChange={e => setQ(e.target.value)}
                placeholder="Search by name or ref…"
                className="w-full pl-7 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none" />
            </div>
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-4 py-3 text-sm text-slate-400">No members found</p>
            ) : filtered.map(m => (
              <button key={m.id} type="button"
                onClick={() => { onChange(String(m.id)); setOpen(false); setQ(''); }}
                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 transition-colors flex items-center justify-between
                  ${String(m.id) === String(value) ? 'bg-blue-50 text-blue-700' : 'text-slate-700'}`}>
                <span>{m.name}</span>
                <span className="text-xs text-slate-400 font-mono">{m.membershipRef}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* Add / Edit modal */
function NoteModal({ note, members, onSave, onClose }) {
  const isEdit = !!note;
  const [form, setForm] = useState({
    member_id: note ? String(note.member_id) : '',
    note_type: note?.note_type || 'Note',
    note_date: note?.note_date
      ? new Date(note.note_date).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10),
    content: note?.content || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.member_id) { setError('Please select a member.'); return; }
    if (!form.content.trim()) { setError('Content is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(form);
    } catch (err) {
      setError(err.message || 'Failed to save note');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-800">{isEdit ? 'Edit Note' : 'Add New Note'}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Member *</label>
              <MemberSelect members={members} value={form.member_id}
                onChange={v => setForm(f => ({ ...f, member_id: v }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Date *</label>
              <input type="date" value={form.note_date}
                onChange={e => setForm(f => ({ ...f, note_date: e.target.value }))}
                className={inputCls} required />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Type *</label>
              <select value={form.note_type}
                onChange={e => setForm(f => ({ ...f, note_type: e.target.value }))}
                className={inputCls}>
                {['Note', 'Letter', 'Important', 'Meeting'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Content *</label>
            <textarea value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              rows={5} placeholder="Write the note content…"
              className={`${inputCls} resize-none`} required />
          </div>
          {error && (
            <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors">
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Note'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function NotesPage() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [modal, setModal] = useState(null); // null | 'add' | noteObject

  const load = async () => {
    setLoading(true);
    try {
      const [n, m] = await Promise.all([
        apiService.getAllNotes(),
        apiService.getMembers({ status: 'all' }),
      ]);
      setNotes(n);
      setMembers(m);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (form) => {
    if (modal && typeof modal === 'object') {
      await apiService.updateMemberNote(modal.member_id, modal.id, form);
    } else {
      await apiService.createMemberNote(form.member_id, form);
    }
    setModal(null);
    await load();
  };

  const handleDelete = async (note) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await apiService.deleteMemberNote(note.member_id, note.id);
      setNotes(ns => ns.filter(n => n.id !== note.id));
    } catch (err) {
      alert(err.message || 'Failed to delete');
    }
  };

  const filtered = notes.filter(n => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      n.content.toLowerCase().includes(q) ||
      n.member_name?.toLowerCase().includes(q) ||
      String(n.membership_ref || '').includes(q);
    const matchType = typeFilter === 'all' || n.note_type === typeFilter;
    return matchSearch && matchType;
  });

  return (
    <div className="p-3 sm:p-6">
      {/* Page header */}
      <div className="flex items-start justify-between mb-5 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-0.5">Member Notes & Communication</h1>
          <p className="text-slate-500 text-sm">Log important notes, letters, and communications.</p>
        </div>
        <button onClick={() => setModal('add')}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm flex-shrink-0">
          <Plus className="w-4 h-4" /> Add New Note
        </button>
      </div>

      {/* Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Card header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2 text-slate-700 font-semibold">
            <FileText className="w-4 h-4 text-slate-500" />
            All Notes ({filtered.length})
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search notes, member name or ref…"
                className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
            </div>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
              className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none text-slate-700 bg-white">
              <option value="all">All Types</option>
              <option value="Note">Note</option>
              <option value="Letter">Letter</option>
              <option value="Important">Important</option>
              <option value="Meeting">Meeting</option>
            </select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3" />
            <span className="text-slate-400 text-sm">Loading notes…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <FileText className="w-10 h-10 mx-auto mb-3 text-slate-200" />
            <p className="text-sm font-medium">{notes.length === 0 ? 'No notes yet' : 'No notes match your search'}</p>
            {notes.length === 0 && (
              <button onClick={() => setModal('add')}
                className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium">
                + Add the first note
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-48">Member (Ref)</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Content</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-20 hidden sm:table-cell">Attachment</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(note => (
                  <tr key={note.id} className="hover:bg-slate-50 transition-colors group">
                    <td className="px-5 py-3.5 text-slate-600 whitespace-nowrap">
                      {new Date(note.note_date).toLocaleDateString('en-GB', { day:'2-digit', month:'2-digit', year:'numeric' })}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${TYPE_COLORS[note.note_type] || TYPE_COLORS.Note}`}>
                        {note.note_type}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <button
                        onClick={() => navigate(`/members/${note.member_id}`)}
                        className="text-left group/member">
                        <p className="font-medium text-slate-800 group-hover/member:text-blue-600 transition-colors flex items-center gap-1">
                          {note.member_name}
                          <ArrowUpRight className="w-3 h-3 opacity-0 group-hover/member:opacity-100 transition-opacity" />
                        </p>
                        <p className="text-xs text-slate-400 font-mono mt-0.5">{note.membership_ref}</p>
                      </button>
                    </td>
                    <td className="px-4 py-3.5 text-slate-700 max-w-xs">
                      <span className="line-clamp-2 leading-relaxed" title={note.content}>
                        {note.content}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-slate-400 text-xs hidden sm:table-cell">None</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setModal(note)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit note">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(note)}
                          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete note">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      {modal !== null && (
        <NoteModal
          note={modal === 'add' ? null : modal}
          members={members}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
